// Main-battery fire control: a faithful-in-spirit model of the 1944 Mk 38
// system, built around the Mk 8 rangekeeper's actual working method —
// RATE CONTROL.
//
//   DESIGNATE  a contact is handed to the director (from CIC radar or optics)
//   TRACK      the Mk 38 trains on it; ranges feed in (radar or optical cuts)
//   RATES      the rangekeeper dead-reckons the target from ESTIMATED target
//              course & speed. Only one course/speed combination makes the
//              GENERATED range and bearing track the OBSERVED ones — finding
//              it is the solution. The FT crew grinds toward it on their own;
//              a human on the rangekeeper (plot, third deck) can beat them.
//   READY      turrets slewed and matched, batteries released
//   FIRE       salvo away (state holds through time of flight)
//   SPOT       splashes observed, corrections cranked back into the plot
//
// Solution quality is no longer a timer — it is measured agreement between
// generated and observed motion. Bad rate estimates physically miss: the
// salvo is aimed with the plot's OWN estimate of target course and speed,
// so a 15-knot rate error at a 40-second time of flight is a 300-meter miss.

import * as THREE from "three";
import { KTS } from "./const.js";
import { solveElevation, solveForHeight } from "./guns.js";
import { randn } from "./guns.js";

const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));

export const FC_STEPS = ["DESIGNATE", "TRACK", "RATES", "READY", "FIRE", "SPOT"];

export class FireControl {
  constructor(G) {
    this.G = G;
    this.state = "idle"; // idle|track|solution|ready|fire|spot
    this.target = null;
    this.quality = 0;
    this.rangeBias = 0;      // meters of systematic error the plot hasn't found yet
    this.bearingBias = 0;    // radians
    this.solution = null;    // {bearing, range, tof, elev}
    this.salvoTimer = 0;
    this.awaitingSplash = false;
    this.splashes = [];
    this.spotClock = 0;
    this.radarRanging = true;
    this.rapidFire = false;
    this.lastCut = 1e9;
    this.directorManned = false;
    this.directorOnTarget = false;
    this.plotManned = false;   // stations.js sets while the rangekeeper is manned
    this.salvosFired = 0;
    this.straddles = 0;
    this.hits = 0;

    // --- rate control state -------------------------------------------------
    this.est = { course: 0, speed: 0 };   // the plot's target course/speed (world rad, m/s)
    this.autoErr = { course: 0, speed: 0 }; // FT crew's residual estimate error
    this.genRange = 0;        // generated (dead-reckoned) present range
    this.genRangeRate = 0;    // m/s, + opening
    this.obsRangeRate = 0;    // smoothed observed range rate
    this.genBearingRate = 0;  // rad/s
    this.obsBearingRate = 0;
    this.rateErr = 0;         // |est velocity - true velocity| m/s
    this.boardT = 0;          // plot-board clock
    this.pendingSpot = null;  // manned-director spot call awaiting the key
    this.history = [];        // {t, obs?, gen} range samples for the time-range plot
    this.salvoMarks = [];     // {t, range, kind:"salvo"|"splash"}
    this._obsAcc = 0;
    this._lastObsRange = null;
    this._lastObsT = 0;
    this._lastObsBearing = null;
    this._hintClock = 0;
    this._staleLog = 0;
  }

  designate(enemy, source) {
    if (!enemy || enemy.sunk) return false;
    this.target = enemy;
    this.state = "track";
    this.quality = source === "radar" ? 0.16 : 0.1;
    const range = this.targetRange();
    this.rangeBias = randn() * 900 + (source === "radar" ? 0 : 300);
    this.bearingBias = randn() * 0.008;
    // The spotter's first guess at target angle and speed — deliberately
    // rough. Rate control is the work of refining it.
    this.est.course = wrap((enemy.heading ?? 0) + randn() * 0.6);
    this.est.speed = Math.max(0, (enemy.speed ?? 0) * (1 + randn() * 0.35) + randn() * 0.6);
    this.autoErr.course = randn() * 0.10;
    this.autoErr.speed = randn() * 1.2;
    this.genRange = range + this.rangeBias * 0.5;
    this.pendingSpot = null;
    this.history.length = 0;
    this.salvoMarks.length = 0;
    this.boardT = 0;
    this._lastObsRange = null;
    this._lastObsBearing = null;
    this.G.hud.log(`PLOT: TRACKING ${enemy.name} — BEARING ${this.bearingText()} RANGE ${Math.round(range / 100) * 100} M`);
    if (!enemy.shore) {
      this.G.hud.log(`DIRECTOR, PLOT: TARGET ANGLE ESTIMATED — COURSE ${degText(this.est.course)}, SPEED ${(this.est.speed / KTS).toFixed(0)} KTS. RATE CONTROL TO REFINE.`);
    }
    this.G.audio.ding();
    return true;
  }

  dropTarget(reason) {
    if (!this.target) return;
    this.G.hud.log(`PLOT: TRACK DROPPED${reason ? " — " + reason : ""}`, "bad");
    this.target = null;
    this.state = "idle";
    this.quality = 0;
    this.pendingSpot = null;
    this.G.guns.setOrder(null);
  }

  // true target geometry (world)
  targetRange() {
    const p = this.G.shipWorldPos();
    return Math.hypot(this.target.pos.x - p.x, this.target.pos.y - p.z);
  }
  targetBearing() {
    const p = this.G.shipWorldPos();
    return Math.atan2(this.target.pos.x - p.x, this.target.pos.y - p.z);
  }
  bearingText() {
    let deg = Math.round(((this.targetBearing() * 180 / Math.PI) % 360 + 360) % 360);
    return String(deg).padStart(3, "0");
  }

  // Adjustments from the rangekeeper (stations.js). course in rad, speed m/s.
  adjustRates(dCourse, dSpeed) {
    if (!this.target) return;
    this.est.course = wrap(this.est.course + dCourse);
    this.est.speed = Math.max(0, Math.min(45 * KTS, this.est.speed + dSpeed));
  }

  // Re-sync the generated range to the latest observed range (the range
  // operator's crank). Doesn't fix the rates — only where the pen sits.
  zeroPlot() {
    if (!this.target) return;
    if (this._lastObsRange !== null) {
      this.genRange = this._lastObsRange;
      this.G.hud.log("PLOT: GENERATED RANGE ZEROED TO OBSERVED");
      this.G.audio.ding();
    } else {
      this.G.hud.log("PLOT: NO OBSERVED RANGE TO ZERO AGAINST", "bad");
    }
  }

  // A range cut from the optical rangefinder minigame. err01: 0 = perfect.
  rangeCut(err01) {
    if (!this.target) return;
    const range = this.targetRange();
    const cutErr = err01 * range * 0.05 * (Math.random() < 0.5 ? -1 : 1);
    // a good cut pulls the plot's range bias toward truth
    const w = 0.55 * (1 - err01);
    this.rangeBias = this.rangeBias * (1 - w) + cutErr * w;
    this.lastCut = 0;
    // the cut is an observation: it lands on the time-range plot
    const obs = range + cutErr;
    this.recordObs(obs, 3.5);
    this.G.hud.log(`DIRECTOR: RANGE ${(Math.round(obs / 50) * 50).toLocaleString()} — MARK!`);
    this.G.audio.ding();
  }

  recordObs(obsRange, dotSize = 2) {
    this._lastObsRange = obsRange;
    this._lastObsT = this.boardT;
    this.history.push({ t: this.boardT, obs: obsRange, gen: this.genRange, dot: dotSize });
  }

  hasRangeData() {
    return (this.radarRanging && this.G.damage.radarAlive) || this.lastCut < 12;
  }

  update(dt) {
    const G = this.G;
    if (!this.target) return;
    if (this.target.sunk) {
      G.hud.log(`${this.target.name} ${this.target.shore ? "DESTROYED" : "SUNK"} — CHECK FIRE, CHECK FIRE`, "ok");
      this.dropTarget();
      return;
    }

    this.boardT += dt;
    const p = G.shipWorldPos();
    const s = G.shipState;
    const range = this.targetRange();
    const bearing = this.targetBearing();

    // --- rate control: generated vs observed --------------------------------
    // Own-ship velocity is measured (gyro + pitometer) and trusted; the target
    // estimate is the suspect part, exactly as on the real Mk 8.
    const ownVx = Math.sin(s.heading) * s.speed, ownVz = Math.cos(s.heading) * s.speed;
    const estVx = Math.sin(this.est.course) * this.est.speed, estVz = Math.cos(this.est.course) * this.est.speed;
    const truVx = Math.sin(this.target.heading ?? 0) * (this.target.speed ?? 0);
    const truVz = Math.cos(this.target.heading ?? 0) * (this.target.speed ?? 0);
    this.rateErr = Math.hypot(estVx - truVx, estVz - truVz);
    const losX = Math.sin(bearing), losZ = Math.cos(bearing);
    this.genRangeRate = (estVx - ownVx) * losX + (estVz - ownVz) * losZ;
    const truRangeRate = (truVx - ownVx) * losX + (truVz - ownVz) * losZ;
    this.genBearingRate = ((estVx - ownVx) * losZ - (estVz - ownVz) * losX) / Math.max(1000, range);
    this.obsBearingRate = ((truVx - ownVx) * losZ - (truVz - ownVz) * losX) / Math.max(1000, range);
    this.genRange += this.genRangeRate * dt;
    if (!Number.isFinite(this.obsRangeRate)) this.obsRangeRate = 0;

    // observed ranges arrive continuously from radar, or as optical cuts
    const radarIn = this.radarRanging && G.damage.radarAlive;
    this._obsAcc += dt;
    if (this._obsAcc > 1.6) {
      this._obsAcc = 0;
      if (radarIn) this.recordObs(range + randn() * 60);
      else this.history.push({ t: this.boardT, gen: this.genRange }); // pen keeps drawing
    }
    while (this.history.length && this.history[0].t < this.boardT - 150) this.history.shift();
    while (this.salvoMarks.length && this.salvoMarks[0].t < this.boardT - 150) this.salvoMarks.shift();
    // the radar range tracker (or a fresh optical cut) yields an observed
    // range rate the operator can needle-match against the generated one
    if (radarIn || this.lastCut < 12) {
      this.obsRangeRate += (truRangeRate + randn() * 0.7 - this.obsRangeRate) * Math.min(1, dt * 0.6);
    } else this.obsRangeRate = this.genRangeRate; // nothing observed: needles agree, falsely

    // --- FT crew work the rates when nobody's on the rangekeeper -----------
    if (!this.plotManned && G.damage.plotAlive) {
      const wantC = wrap((this.target.heading ?? 0) + this.autoErr.course);
      const wantS = Math.max(0, (this.target.speed ?? 0) + this.autoErr.speed);
      const k = Math.min(1, dt / 45); // slow, methodical needle-matching
      this.est.course = wrap(this.est.course + wrap(wantC - this.est.course) * k);
      this.est.speed += (wantS - this.est.speed) * k;
    }

    // --- quality: measured agreement, not a timer ---------------------------
    const rateAcc = Math.max(0, 1 - this.rateErr / 9);
    let obsFactor = 0.45;
    if (radarIn) obsFactor += 0.30;
    // human eyes on the scope only help if there's anything to see
    if (this.directorManned && this.directorOnTarget && (!G.night || G.illum?.isLit(this.target))) obsFactor += 0.25;
    if (this.lastCut < 10) obsFactor += 0.12;
    obsFactor = Math.min(1, obsFactor);
    let qTarget = obsFactor * (0.30 + 0.70 * rateAcc);
    if (!G.damage.plotAlive) qTarget = Math.min(qTarget, 0.25);
    const qRate = qTarget > this.quality ? 0.055 : 0.14;
    this.quality = Math.max(0.08, this.quality + (qTarget - this.quality) * Math.min(1, qRate * dt * 6));
    // a maneuvering target makes the rates stale — call it out once
    if (Math.abs(this.target.turnRate ?? 0) > 0.002 && this.rateErr > 4 && this.boardT - this._staleLog > 25) {
      this._staleLog = this.boardT;
      G.hud.log("PLOT: TARGET UNDER HELM — RATES GOING STALE, RE-WORK THE SOLUTION", "bad");
    }
    // biases decay toward zero as good range data settles the plot — slowly,
    // so spotting the fall of shot stays the fastest way to find the error.
    // The plot holds its hand while a salvo is being observed: applying rate
    // corrections and spots at the same time is the "pyramiding" the gunnery
    // manuals warn about, and it double-corrects the same error.
    if (!this.awaitingSplash && !this.pendingSpot) {
      const settle = 0.006 + (radarIn ? 0.012 : 0);
      this.rangeBias -= this.rangeBias * settle * dt;
      this.bearingBias -= this.bearingBias * (settle * 1.4) * dt;
    }
    this.lastCut += dt;
    this.salvoTimer += dt;

    // an unanswered spot call falls back to the air spot's weaker correction
    if (this.pendingSpot) {
      this.pendingSpot.timer -= dt;
      if (this.pendingSpot.timer <= 0) {
        const ps = this.pendingSpot;
        this.pendingSpot = null;
        this.rangeBias -= ps.along * 0.55;
        this.bearingBias -= Math.atan2(ps.across, Math.max(2000, range)) * 0.55;
        G.hud.log("SPOT WINDOW PASSED — AIR SPOT CORRECTION APPLIED", "bad");
      }
    }

    // --- generated firing solution: lead from the plot's OWN rates ---------
    // The rangekeeper advances its believed target by its estimated course
    // and speed. If the estimate is wrong, the shells go where the estimate
    // says — not where the target does.
    let aimX = this.target.pos.x, aimZ = this.target.pos.y;
    let tof = 30;
    for (let i = 0; i < 3; i++) {
      const r = Math.hypot(aimX - p.x, aimZ - p.z);
      tof = solveElevation(r).tof;
      aimX = this.target.pos.x + estVx * tof;
      aimZ = this.target.pos.y + estVz * tof;
    }
    let solRange = Math.hypot(aimX - p.x, aimZ - p.z);
    const solBearing = Math.atan2(aimX - p.x, aimZ - p.z);
    // shore targets sit above the waterline: aim long so the descending
    // branch of the trajectory crosses the target's altitude on top of it
    solRange = solveForHeight(solRange, this.target.y || 0);
    this.solution = { bearing: solBearing, range: solRange, tof, elev: solveElevation(solRange).elev };

    // --- state machine ---
    switch (this.state) {
      case "track":
        if (this.quality >= 0.5) {
          this.state = "solution";
          G.hud.log("PLOT: SOLUTION — GENERATED RATES MATCH OBSERVED. TURRETS SLEWING.");
          G.guns.setOrder(this.solution);
        } else {
          this._hintClock += dt;
          if (this._hintClock > 22) {
            this._hintClock = 0;
            if (!this.hasRangeData()) G.hud.log("PLOT: NO RANGE DATA — RADAR RANGING IS OUT. MAN THE DIRECTOR AND TAKE CUTS.", "bad");
            else if (rateAcc < 0.6) G.hud.log("PLOT: RATES DON'T MATCH — WORK TARGET COURSE AND SPEED AT THE RANGEKEEPER (T MENU).", "bad");
          }
        }
        break;
      case "solution": {
        G.guns.setOrder(this.solution);
        if (this.quality < 0.35) { this.state = "track"; G.guns.setOrder(null); break; }
        const ready = G.guns.readyTurrets().length;
        if (ready > 0) {
          this.state = "ready";
          G.hud.log(`PLOT: BATTERY RELEASED — ${ready} TURRET${ready > 1 ? "S" : ""} READY. FIRE WHEN READY (F)`, "ok");
          G.audio.bosunPipe();
        }
        break;
      }
      case "ready":
        G.guns.setOrder(this.solution);
        if (this.quality < 0.35) { this.state = "track"; G.guns.setOrder(null); break; }
        if (G.guns.readyTurrets().length === 0 && G.guns.reload <= 0) this.state = "solution";
        if (this.rapidFire && G.guns.readyTurrets().length > 0) this.fire();
        break;
      case "fire":
        if (this.salvoTimer > 2) { this.state = "spot"; this.spotClock = 0; }
        break;
      case "spot":
        this.spotClock += dt;
        if (this.awaitingSplash && this.spotClock > (this.solution?.tof ?? 40) + 12) this.scoreSalvo();
        if (!this.awaitingSplash && this.spotClock > 3) {
          this.state = this.quality >= 0.5 ? "solution" : "track";
        }
        break;
    }

    // range gate
    if (range > 42000) this.dropTarget("TARGET BEYOND RADAR COVERAGE");
  }

  canFire() {
    return (this.state === "ready" || this.state === "spot" || this.state === "solution")
      && this.G.guns.readyTurrets().length > 0;
  }

  fire(opts = {}) {
    const G = this.G;
    if (!this.canFire()) return false;
    const err = {
      bearing: this.bearingBias * (1.5 - this.quality),
      range: this.rangeBias * (1.6 - this.quality),
    };
    const fired = G.guns.fireSalvo(err, opts);
    if (!fired) return false;
    this.salvosFired++;
    this.state = "fire";
    this.salvoTimer = 0;
    this.awaitingSplash = true;
    this.splashes = [];
    this.expectedShells = fired.length * 3;
    this.salvoMarks.push({ t: this.boardT, range: this.solution.range, kind: "salvo" });
    G.audio.salvoBuzzer?.();
    G.hud.log(`${fired.join(", ")} — SALVO! ${this.expectedShells} GUNS, RANGE ${(Math.round(this.solution.range / 100) * 100).toLocaleString()} M, TOF ${Math.round(this.solution.tof)} S`);
    if (!this._vHinted) {
      this._vHinted = true;
      G.hud.log("PRESS V — RIDE THE SALVO OUT AND WATCH IT LAND", "ok");
    }
    return true;
  }

  // Called by guns.js for every one of our splashes/hits.
  reportSplash(shell, at, hitResult) {
    if (!this.awaitingSplash || !this.target) return;
    this.splashes.push({ at: at.clone(), hit: !!hitResult });
    if (hitResult) {
      this.hits++;
      this.G.hud.log(`HIT! ${this.target.name} — ${hitResult.text}`, "ok");
    }
    if (this.splashes.length >= this.expectedShells) this.scoreSalvo();
  }

  scoreSalvo() {
    this.awaitingSplash = false;
    const G = this.G;
    if (!this.target || this.target.sunk) return;
    const p = G.shipWorldPos();
    const tb = this.targetBearing();
    const losX = Math.sin(tb), losZ = Math.cos(tb);
    let along = 0, across = 0, n = 0, hits = 0;
    for (const sp of this.splashes) {
      const dx = sp.at.x - this.target.pos.x, dz = sp.at.z - this.target.pos.y;
      along += dx * losX + dz * losZ;     // + = over
      across += dx * losZ - dz * losX;    // + = right (roughly)
      n++; if (sp.hit) hits++;
    }
    if (!n) return;
    const alongs = this.splashes.map((sp) => {
      const dx = sp.at.x - this.target.pos.x, dz = sp.at.z - this.target.pos.y;
      return dx * losX + dz * losZ;
    });
    along /= n; across /= n;
    // the splash group lands on the time-range plot too
    this.salvoMarks.push({ t: this.boardT, range: this.targetRange() + along, kind: "splash" });
    const shore = !!this.target.shore;
    const straddled = along <= 120 && along >= -120;
    if (straddled) this.straddles++;
    this.quality = Math.min(1, this.quality + (straddled ? 0.10 : 0.04));

    // A manned director spots ship targets itself: the splashes come up on
    // the Mk 8 radar's spot scope and the correction is YOUR call — key it
    // in like a spotter (deflection then range, in the real order). Miss the
    // window and the air spot's weaker correction goes in instead.
    if (this.directorManned && !shore) {
      const pipNoise = G.damage.radarAlive ? 45 : 170;
      this.pendingSpot = {
        along, across,
        range: 0, defl: 0,       // the player's keyed spot
        timer: 18,
        pips: alongs.map((a) => a + randn() * pipNoise),
        mpiDefl: across + randn() * (pipNoise * 1.2),
        hits,
      };
      G.hud.log(`DIRECTOR: SPLASHES ON THE SPOT SCOPE${hits ? ` — ${hits} HIT${hits > 1 ? "S" : ""}` : ""} — KEY THE SPOT AND SEND IT (C)`, hits ? "ok" : undefined);
      return;
    }

    // naval spotting says OVER/SHORT/STRADDLE; a shore spotter corrects with DROP/ADD
    const overShort = along > 120 ? (shore ? `DROP ${fmt(along)}` : `OVER ${fmt(along)}`)
      : along < -120 ? (shore ? `ADD ${fmt(-along)}` : `SHORT ${fmt(-along)}`)
      : (shore ? "ON TOP" : "STRADDLE");
    const leftRight = across > 100 ? `RIGHT ${fmt(across)}` : across < -100 ? `LEFT ${fmt(-across)}` : "ON";
    const who = shore ? "SPOTTER" : "AIR SPOT";
    G.hud.log(`${who}: ${overShort}${leftRight === "ON" ? "" : ", " + leftRight}${hits ? ` — ${hits} HIT${hits > 1 ? "S" : ""}` : ""}`, straddled || hits ? "ok" : undefined);
    const w = 0.55;
    this.rangeBias -= along * w;
    this.bearingBias -= Math.atan2(across, this.targetRange()) * w;
  }

  // The director officer's spot, keyed at the station. Signs: +range = ADD
  // (shoot longer), +defl = RIGHT. A call that matches the actual miss
  // cancels it almost entirely — sharper than any air spot.
  adjustSpot(dRange, dDefl) {
    const ps = this.pendingSpot;
    if (!ps) return;
    ps.range = Math.max(-1600, Math.min(1600, ps.range + dRange));
    ps.defl = Math.max(-1600, Math.min(1600, ps.defl + dDefl));
  }

  applyPlayerSpot() {
    const ps = this.pendingSpot;
    if (!ps || !this.target) return;
    this.rangeBias += ps.range * 0.95;
    this.bearingBias += Math.atan2(ps.defl * 0.95, Math.max(2000, this.targetRange()));
    const err = Math.abs(ps.range + ps.along);
    const dText = ps.defl ? `${ps.defl > 0 ? "RIGHT" : "LEFT"} ${Math.abs(ps.defl)}, ` : "";
    const rText = ps.range ? `${ps.range > 0 ? "ADD" : "DROP"} ${Math.abs(ps.range)}` : "NO CHANGE";
    this.G.hud.log(`PLOT: SPOT APPLIED — ${dText}${rText}${err < 150 ? ". GOOD CALL." : ""}`, err < 150 ? "ok" : undefined);
    if (err < 150) this.quality = Math.min(1, this.quality + 0.08);
    this.G.audio.ding();
    this.pendingSpot = null;
  }
}

function fmt(m) { return `${Math.round(m / 50) * 50}`; }
function degText(r) {
  return String(Math.round((((r * 180 / Math.PI) % 360) + 360) % 360)).padStart(3, "0");
}
